import type { AnnotationRef, Ticket } from '@vd/types';

/**
 * The cached ticket with a newer annotation row for `targetId`.
 *
 * The ticket page does not refetch when annotations change (a refetch
 * re-signs the screenshot and it flashes), so the canvas writes each version
 * it learns of into the cached ticket instead; otherwise a return to the
 * ticket within the query's stale time opens on the shapes from the last
 * fetch. Only ever forward: a save's answer can arrive after a live event
 * that already carried a later version, and putting the older one back
 * would reopen the ticket on shapes the server has moved past.
 */
export function withAnnotationRow(
  ticket: Ticket,
  targetId: string,
  snapshot: unknown,
  version: number,
  createdBy: string,
  now: string,
): Ticket {
  const row = ticket.annotations.find((annotation) => annotation.targetId === targetId);
  if (row?.version !== undefined && row.version >= version) return ticket;
  // A ticket fetched before its first save has no row; the id is a
  // placeholder, since nothing reads it before the next fetch replaces it.
  const next: AnnotationRef = row
    ? { ...row, snapshot, version }
    : { id: `an-local-${targetId}`, targetId, createdBy, updatedAt: now, snapshot, version };
  return {
    ...ticket,
    annotations: [...ticket.annotations.filter((annotation) => annotation !== row), next],
  };
}
